import { useState } from "react";
import { Link } from "react-router-dom";
import PillButton from "@/components/base/PillButton";
import HomeHero from "@/components/feature/HomeHero";
import TerritorySection from "./components/TerritorySection";
import VocesSection from "./components/VocesSection";
import TimelineSection from "./components/TimelineSection";
import { useSupabaseData } from "@/hooks/useSupabaseData";
import { useHomepageConfig } from "@/hooks/useHomepageConfig";
import { useHero } from "@/hooks/useHero";
import { heroContent } from "@/data/heroContent";
import { toDriveDirectUrl } from "@/lib/utils";
import { useEffect } from "react";

const heroFallback = heroContent.find((h) => h.page === "inicio")!;

interface NoticiaDB {
  id: number;
  titulo: string;
  extracto?: string;
  imagen?: string;
  fecha?: string;
  comuna?: string;
  categoria?: string;
  segmento?: string;
}

export default function Home() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [openFilter, setOpenFilter] = useState<number | null>(null);
  const { data: noticias } = useSupabaseData<NoticiaDB>("noticias", []);
  const { data: config } = useHomepageConfig();
  const { data: inicioHero } = useHero("inicio", heroFallback);

  // Update page title from hero seo_title
  useEffect(() => {
    const seoTitle = inicioHero?.seo_title ?? heroFallback.seoTitle ?? "MásMed Tarapacá";
    if (seoTitle) {
      document.title = seoTitle;
    }
  }, [inicioHero]);

  const heroSlides =
    config.hero_slides.length > 0
      ? config.hero_slides
      : [
          "https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/1c3df1a8-fbeb-467a-a1a4-2051fc16c0d1_02.png?v=286519a9ca59e07b89846d5d13ec3dad",
          "https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/2a226ddc-d454-4f80-8fa4-d4df7f221fb8_compressed_09.webp",
          "https://storage.readdy-site.link/project_files/dd803486-334c-44db-be94-f45bc3cd2f43/57a05ae3-ae3d-4c15-9eae-16d46723c477_07.png?v=e8557c2d0170829d60ecc2f09546ee6e",
        ];

  const filtrosVisibles =
    config.filtros_config && config.filtros_config.length > 0
      ? config.filtros_config.filter((f) => f.label && f.options.length > 0)
      : [];

  return (
    <div>
      {/* Home-specific hero */}
      <HomeHero
        title={config.hero_title}
        subtitle={config.hero_subtitle || undefined}
        slides={heroSlides}
        ctaText={config.hero_cta_text}
        ctaLink={config.hero_cta_link}
        institutionalText={config.hero_institutional_text}
      />

      {/* Actualidad + ¿Qué es MásMed Tarapacá? — Layout de dos columnas */}
      <section id="actualidad" className="py-16 md:py-20 lg:py-24 bg-white">
        <div className="max-w-[1180px] mx-auto px-4 md:px-8 lg:px-12">
          <div className="flex flex-col lg:flex-row gap-10 md:gap-14 lg:gap-16 items-start">

            {/* Columna izquierda: Actualidad (~45%) */}
            <div className="w-full lg:w-[45%] flex-shrink-0">
              <h2 className="text-2xl md:text-[28px] text-masmed-gray-600 mb-6 md:mb-8 tracking-tight">
                {config.actualidad_title}
              </h2>

              {/* Filtros dinámicos desde homepage_config */}
              <div className="flex items-center justify-start gap-2 md:gap-3 mb-8 md:mb-10">
                <button
                  type="button"
                  className="w-12 h-12 flex-shrink-0 rounded-xl border border-masmed-gray-300 bg-white flex items-center justify-center shadow-sm hover:border-primary hover:text-primary transition-colors duration-200 cursor-pointer"
                  aria-label="Filtros"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-equalizer-line text-masmed-gray-600 text-lg"></i>
                  </span>
                </button>
                {filtrosVisibles.map((filtro, fi) => (
                  <div key={fi} className="relative">
                    <button
                      type="button"
                      onClick={() => setOpenFilter(openFilter === fi ? null : fi)}
                      className="inline-flex items-center gap-2 px-5 py-3 rounded-pill border border-masmed-gray-300 bg-white text-masmed-gray-700 text-sm md:text-base shadow-sm hover:border-primary hover:text-primary transition-colors duration-200 cursor-pointer whitespace-nowrap"
                      style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                    >
                      <span>{filtro.label}</span>
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className={`ri-arrow-down-s-line text-masmed-gray-400 transition-transform duration-200 ${openFilter === fi ? "rotate-180" : ""}`}></i>
                      </span>
                    </button>
                    {openFilter === fi && filtro.options.length > 0 && (
                      <div className="absolute top-full left-0 mt-1 bg-white border border-masmed-gray-200 rounded-xl shadow-lg z-30 min-w-[160px] py-2">
                        {filtro.options.map((opt, oi) => (
                          <button
                            key={oi}
                            type="button"
                            onClick={() => setOpenFilter(null)}
                            className="block w-full text-left px-4 py-2 text-sm text-masmed-gray-700 hover:bg-primary/5 hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
                          >
                            {opt}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Tarjetas de Actualidad */}
              <div className="flex flex-col gap-4 md:gap-5">
                <div className="grid grid-cols-2 gap-4 md:gap-5">
                  {noticias.slice(0, 2).map((noticia) => (
                    <article
                      key={noticia.id}
                      className="bg-white rounded-2xl overflow-hidden flex flex-col border border-masmed-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-card-hover"
                    >
                      <div className="h-36 md:h-44 lg:h-48 w-full overflow-hidden bg-masmed-gray-100">
                        <img
                          src={toDriveDirectUrl(noticia.imagen || "")}
                          alt={noticia.titulo}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="p-4 md:p-5 flex-1 flex flex-col">
                        <div className="flex items-center justify-between gap-2 mb-2">
                          <span className="text-primary text-xs md:text-sm"
                          style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                            {noticia.comuna || "—"}
                          </span>
                          <span className="text-masmed-gray-400 text-xs md:text-sm">
                            {noticia.fecha || "—"}
                          </span>
                        </div>
                        <h3 className="text-sm md:text-base text-masmed-gray-900 leading-snug mb-2 line-clamp-2"
                        style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}>
                          {noticia.titulo}
                        </h3>
                        <p className="text-masmed-gray-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3 text-justify">
                          {noticia.extracto || "—"}
                        </p>
                        <div className="mt-auto flex justify-end">
                          <Link
                            to={`/noticias/${noticia.id}`}
                            className="inline-flex items-center bg-primary text-white text-xs md:text-sm px-3 py-1.5 rounded-md hover:bg-primary-dark transition-colors duration-200"
                            style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                          >
                            Ver más
                            <span className="w-4 h-4 flex items-center justify-center ml-1">
                              <i className="ri-arrow-right-line text-white"></i>
                            </span>
                          </Link>
                        </div>
                      </div>
                    </article>
                  ))}
                </div>

                {noticias.slice(2, 3).map((noticia) => (
                  <article
                    key={noticia.id}
                    className="bg-white rounded-2xl overflow-hidden flex flex-row border border-masmed-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-card-hover"
                  >
                    <div className="w-[140px] md:w-[180px] h-32 md:h-40 flex-shrink-0 overflow-hidden bg-masmed-gray-100">
                      <img
                        src={toDriveDirectUrl(noticia.imagen || "")}
                        alt={noticia.titulo}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-4 md:p-5 flex-1 flex flex-col justify-center min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-primary text-xs md:text-sm"
                        style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                          {noticia.comuna || "—"}
                        </span>
                        <span className="text-masmed-gray-400 text-xs md:text-sm">
                          {noticia.fecha || "—"}
                        </span>
                      </div>
                      <h3 className="text-sm md:text-base font-bold text-masmed-gray-900 leading-snug mb-2 line-clamp-2">
                        {noticia.titulo}
                      </h3>
                      <p className="text-masmed-gray-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3 text-justify">
                        {noticia.extracto || "—"}
                      </p>
                      <div className="mt-auto flex justify-end">
                        <Link
                          to={`/noticias/${noticia.id}`}
                          className="inline-flex items-center bg-primary text-white text-xs md:text-sm px-3 py-1.5 rounded-md hover:bg-primary-dark transition-colors duration-200"
                          style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                        >
                          Ver más
                          <span className="w-4 h-4 flex items-center justify-center ml-1">
                            <i className="ri-arrow-right-line text-white"></i>
                          </span>
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}

                {noticias.slice(3, 4).map((noticia) => (
                  <article
                    key={noticia.id}
                    className="bg-white rounded-2xl overflow-hidden flex flex-row border border-masmed-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-card-hover"
                  >
                    <div className="w-[140px] md:w-[180px] h-32 md:h-40 flex-shrink-0 overflow-hidden bg-masmed-gray-100">
                      <img
                        src={toDriveDirectUrl(noticia.imagen || "")}
                        alt={noticia.titulo}
                        className="w-full h-full object-cover object-top"
                      />
                    </div>
                    <div className="p-4 md:p-5 flex-1 flex flex-col justify-center min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-2">
                        <span className="text-primary text-xs md:text-sm"
                        style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                          {noticia.comuna || "—"}
                        </span>
                        <span className="text-masmed-gray-400 text-xs md:text-sm">
                          {noticia.fecha || "—"}
                        </span>
                      </div>
                      <h3 className="text-sm md:text-base font-bold text-masmed-gray-900 leading-snug mb-2 line-clamp-2">
                        {noticia.titulo}
                      </h3>
                      <p className="text-masmed-gray-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3 text-justify">
                        {noticia.extracto || "—"}
                      </p>
                      <div className="mt-auto flex justify-end">
                        <Link
                          to={`/noticias/${noticia.id}`}
                          className="inline-flex items-center bg-primary text-white text-xs md:text-sm px-3 py-1.5 rounded-md hover:bg-primary-dark transition-colors duration-200"
                          style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                        >
                          Ver más
                          <span className="w-4 h-4 flex items-center justify-center ml-1">
                            <i className="ri-arrow-right-line text-white"></i>
                          </span>
                        </Link>
                      </div>
                    </div>
                  </article>
                ))}

                {/* Botón Ver Todo con desplegable */}
                <div className="flex flex-col items-center mt-2">
                  <button
                    type="button"
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary bg-white text-primary text-sm md:text-base hover:bg-primary hover:text-white transition-all duration-200 cursor-pointer whitespace-nowrap"
                    style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                  >
                    {isExpanded ? "Ver menos" : "Ver Todo"}
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className={`ri-arrow-down-s-line transition-transform duration-300 ${isExpanded ? "rotate-180" : ""}`}></i>
                    </span>
                  </button>

                  <div
                    className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${isExpanded ? "max-h-[1200px] opacity-100 mt-4" : "max-h-0 opacity-0 mt-0"}`}
                  >
                    <div className="flex flex-col gap-4">
                      {noticias.slice(4).map((noticia) => (
                        <article
                          key={noticia.id}
                          className="bg-white rounded-2xl overflow-hidden flex flex-row border border-masmed-gray-200 shadow-sm transition-shadow duration-200 hover:shadow-card-hover"
                        >
                          <div className="w-[120px] md:w-[160px] h-28 md:h-36 flex-shrink-0 overflow-hidden bg-masmed-gray-100">
                            <img
                              src={toDriveDirectUrl(noticia.imagen || "")}
                              alt={noticia.titulo}
                              className="w-full h-full object-cover object-top"
                            />
                          </div>
                          <div className="p-4 md:p-5 flex-1 flex flex-col justify-center min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-2">
                              <span className="text-primary text-xs md:text-sm"
                              style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                                {noticia.comuna || "—"}
                              </span>
                              <span className="text-masmed-gray-400 text-xs md:text-sm">
                                {noticia.fecha || "—"}
                              </span>
                            </div>
                            <h3 className="text-sm md:text-base font-bold text-masmed-gray-900 leading-snug mb-2 line-clamp-2">
                              {noticia.titulo}
                            </h3>
                            <p className="text-masmed-gray-500 text-xs md:text-sm leading-relaxed line-clamp-2 mb-3 text-justify">
                              {noticia.extracto || "—"}
                            </p>
                            <div className="mt-auto flex justify-end">
                              <Link
                                to={`/noticias/${noticia.id}`}
                                className="inline-flex items-center bg-primary text-white text-xs md:text-sm px-3 py-1.5 rounded-md hover:bg-primary-dark transition-colors duration-200"
                                style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 700 }}
                              >
                                Ver más
                                <span className="w-4 h-4 flex items-center justify-center ml-1">
                                  <i className="ri-arrow-right-line text-white"></i>
                                </span>
                              </Link>
                            </div>
                          </div>
                        </article>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Columna derecha: ¿Qué es MásMed Tarapacá? (~55%) */}
            <div className="w-full lg:w-[55%] flex-shrink-0">
              <h2 className="text-2xl md:text-[28px] text-masmed-gray-600 mb-3 tracking-tight">
                {config.que_es_title}
              </h2>

              {/* Imagen con banda naranja */}
              <div className="relative rounded-2xl overflow-hidden mb-4">
                <img
                  src={toDriveDirectUrl(config.que_es_image || "")}
                  alt="Territorio Tarapacá"
                  className="w-full h-64 md:h-80 lg:h-96 object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-primary py-3 px-4">
                  <p className="text-white text-sm md:text-base text-center"
                  style={{ fontFamily: "'Arial', 'Helvetica', sans-serif", fontWeight: 600 }}>
                    {config.que_es_banner_text}
                  </p>
                </div>
              </div>

              {/* Texto en dos columnas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 mt-2">
                <p className="text-masmed-gray-600 text-sm md:text-base leading-relaxed text-justify">
                  {config.que_es_left_text}
                </p>
                <p className="text-masmed-gray-600 text-sm md:text-base leading-relaxed text-justify">
                  {config.que_es_right_text}
                </p>
              </div>

              {/* Botón centrado */}
              <div className="text-center mt-8 md:mt-10">
                <PillButton variant="primary" href={config.que_es_button_link}>
                  {config.que_es_button_text}
                  <i className="ri-arrow-right-line ml-2"></i>
                </PillButton>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Territorio */}
      {config.inicio_territorio_visible && (
        <TerritorySection territorioTitle={config.territorio_title} />
      )}

      {/* Voces que Construyen Salud */}
      <VocesSection vocesTitle={config.voces_title} />

      {/* Línea de tiempo */}
      <TimelineSection timelineTitle={config.timeline_title} />
    </div>
  );
}